import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, ChevronLeftOutline, ChevronRightOutline, IconButton, rem, useBreakpointValue, useColorModeValue } from '@bonusly/design-system';

import { useAnnouncementDismissals } from '../../hooks/use-announcement-dismissals';
import { Announcement } from '../../types/announcements';
import { AnnouncementCard } from '../announcement-card';
import { EmptyState } from './empty-state';

interface AnnouncementsCarouselContentProps {
	announcements: Announcement[];
	legacy?: boolean; // legacy carousel uses a different layout
	selectedAnnouncementId?: string;
}

const CARD_WIDTH = { base: rem(300), xs: rem(550) };
const CARD_WIDTH_PX = { base: 300, xs: 550 };
const GAP_PX = 12;
const BUTTON_OFFSET_PX = 56; // Space for left arrow button

export const AnnouncementsCarouselContent: FC<AnnouncementsCarouselContentProps> = ({ announcements, legacy = false, selectedAnnouncementId }) => {
	const { t } = useTranslation(['common', 'announcements']);
	const { track } = useAnalytics();
	const alreadyInteractedWithCarousel = useRef(false);
	const { dismissAnnouncement } = useAnnouncementDismissals();
	const isDesktop = useBreakpointValue({ base: false, xs: true });

	// Initialize current index from prop, default to 0
	const getInitialIndex = () => {
		if (!selectedAnnouncementId) return 0;
		const index = announcements.findIndex((a) => a.id === selectedAnnouncementId);
		return index === -1 ? 0 : index;
	};
	const [currentIndex, setCurrentIndex] = useState(getInitialIndex);

	const canGoLeft = currentIndex > 0;
	const canGoRight = currentIndex < announcements.length - 1;

	// Calculate transform offset based on current index
	// When showing cards after the first, offset to leave space for the left arrow button
	const cardWidth = isDesktop ? CARD_WIDTH_PX.xs : CARD_WIDTH_PX.base;
	const baseTranslate = currentIndex * (cardWidth + GAP_PX);
	const translateX = canGoLeft ? baseTranslate - BUTTON_OFFSET_PX : baseTranslate;

	const navigateTo = (newIndex: number) => {
		if (newIndex < 0 || newIndex >= announcements.length) return;
		setCurrentIndex(newIndex);
		dismissAnnouncement(announcements[newIndex].id);
	};

	const trackInteraction = () => {
		// avoid tracking too many interactions
		if (alreadyInteractedWithCarousel.current) return;
		track(AnalyticsEvent.Announcements.AnnouncementCarouselInteraction);
		alreadyInteractedWithCarousel.current = true;
	};

	// When multiple announcements exist, show a peek of the next announcement by not using full width
	const onlyOneAnnouncement = announcements.length === 1;

	// Gradients for carousel fade effect - adapt to color mode
	const leftGradient = useColorModeValue(
		'linear-gradient(90deg, rgb(246 246 249) 0%, rgba(237, 238, 243, 0) 100%)',
		'linear-gradient(90deg, var(--chakra-colors-gray-900) 0%, rgba(23, 25, 35, 0) 100%)'
	);
	const rightGradient = useColorModeValue(
		'linear-gradient(270deg, rgb(246 246 249) 0%, rgba(237, 238, 243, 0) 100%)',
		'linear-gradient(270deg, var(--chakra-colors-gray-900) 0%, rgba(23, 25, 35, 0) 100%)'
	);

	return (
		<Box position="relative" display="flex" alignItems="center" data-testid="announcements-carousel-wrapper">
			{canGoLeft && (
				<>
					{!legacy && <Box position="absolute" left="0" width="12%" height="100%" top="0" bg={leftGradient} zIndex={1} />}
					<IconButton
						data-testid="left-button"
						icon={<ChevronLeftOutline />}
						aria-label={t('common:go_back')}
						borderRadius="full"
						color="white"
						backgroundColor="black"
						_hover={{ backgroundColor: 'black' }}
						zIndex={99}
						position="absolute"
						top="50%"
						left={3}
						transform="translateY(-50%)"
						onClick={() => {
							navigateTo(currentIndex - 1);
							trackInteraction();
						}}
					/>
				</>
			)}
			<Box flex={1} overflowX="hidden" display="flex" gap={5}>
				<Box
					display="flex"
					p={legacy ? rem(16) : 0}
					gap={3}
					flexWrap="nowrap"
					width={onlyOneAnnouncement || !announcements.length ? '100%' : 'auto'}
					minH={!announcements.length ? '200px' : undefined}
					justifyContent={!announcements.length ? 'center' : undefined}
					alignItems={!announcements.length ? 'center' : undefined}
					transform={`translateX(-${translateX}px)`}
					transition="transform 0.3s ease-out"
				>
					{announcements.length ? (
						announcements.map((announcement) => (
							<AnnouncementCard
								key={announcement.id}
								id={`announcement-${announcement.id}`}
								announcement={announcement}
								width={onlyOneAnnouncement ? '100%' : CARD_WIDTH}
							/>
						))
					) : (
						<EmptyState />
					)}
				</Box>
			</Box>
			{canGoRight && (
				<>
					{!legacy && <Box position="absolute" right="0" width="12%" height="100%" top="0" bg={rightGradient} zIndex={1} />}
					<IconButton
						data-testid="right-button"
						icon={<ChevronRightOutline />}
						aria-label={t('common:next')}
						position="absolute"
						borderRadius="full"
						zIndex={99}
						color="white"
						backgroundColor="black"
						_hover={{ backgroundColor: 'black' }}
						top="50%"
						right={3}
						transform="translateY(-50%)"
						onClick={() => {
							navigateTo(currentIndex + 1);
							trackInteraction();
						}}
					/>
				</>
			)}
		</Box>
	);
};
